// The die on the stage. It covers the stage, so the die can travel over the
// whole board frame and bounce off its edges. `slot` is the box in the stage
// layout where the die comes to rest beside the card. `launchPad` is the roll
// button: the die enters at the point of the frame nearest to it.

import {
  DIE_SPIN_MS,
  REDUCED_MOTION_QUERY,
  TUMBLE_FALLBACK_RADIUS_RATIO,
  TUMBLE_WALL_INSET,
} from '../constants';
import { type DieFaceLabel, landingOrientation } from '../dice/octahedron';
import { type Point, planTumble, poseAt } from '../dice/tumble';
import { createRng } from '../lib/rng';
import { setAttributes, svg } from '../lib/svg';
import { playSound } from '../sound/player';
import { randomSeed } from '../state/settings';
import { createDieSolid } from './DieSolid';

const AWAY_CLASS = 'tumble--away';
const SPIT_CLASSES = ['tumble--spit-a', 'tumble--spit-b'] as const;
const STAGE_TONE_ID_PREFIX = 'stage-die-tone-';

type Arena = {
  width: number;
  height: number;
  radius: number;
  landing: Point;
  /** False when the layout hides the slot. The die then leaves when it lands. */
  hasSlot: boolean;
  /** The center of the roll button. It may be outside the stage. */
  launch: Point;
};

export type TumblingDie = {
  svg: SVGSVGElement;
  /** Rolls the die across the stage. It comes to rest on `face` after `DIE_SPIN_MS`. */
  tumble: (face: number) => void;
  /** Shows the die at rest on `face`. */
  rest: (face: number) => void;
  stop: () => void;
  /** What each face reads. */
  label: (labels: readonly DieFaceLabel[]) => void;
  /** The die squashes once, as a coin of energy leaves it. `count` is which coin of the roll, from zero. */
  spit: (count: number) => void;
};

/** `toneIdPrefix` names the patterns of this die. A second die on the page needs its own. */
export const createTumblingDie = (
  slot: HTMLElement,
  launchPad: HTMLElement,
  toneIdPrefix = STAGE_TONE_ID_PREFIX,
): TumblingDie => {
  const solid = createDieSolid(toneIdPrefix);
  const root = svg('svg', { class: 'tumble', 'aria-hidden': 'true' }, [solid.defs, solid.group]);
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  let frame = 0;

  const measure = (): Arena | null => {
    const stage = root.getBoundingClientRect();
    if (stage.width === 0 || stage.height === 0) return null;
    // One unit of the drawing is one screen pixel, so the dither cells stay whole.
    setAttributes(root, { viewBox: `0 0 ${stage.width} ${stage.height}` });
    const box = slot.getBoundingClientRect();
    const hasSlot = box.width > 0;
    const pad = launchPad.getBoundingClientRect();
    return {
      width: stage.width,
      height: stage.height,
      radius: hasSlot ? box.width / 2 : stage.width * TUMBLE_FALLBACK_RADIUS_RATIO,
      landing: hasSlot
        ? { x: box.left - stage.left + box.width / 2, y: box.top - stage.top + box.height / 2 }
        : { x: stage.width / 2, y: stage.height / 2 },
      hasSlot,
      launch: {
        x: pad.left - stage.left + pad.width / 2,
        y: pad.top - stage.top + pad.height / 2,
      },
    };
  };

  const stop = (): void => {
    cancelAnimationFrame(frame);
    frame = 0;
  };

  // Two classes with the same motion come in turn, so the second coin starts the motion again.
  // The class stays until the next roll. Without a class, the landing would play again.
  const spit = (count: number): void => {
    root.classList.remove(...SPIT_CLASSES);
    root.classList.add(SPIT_CLASSES[count % SPIT_CLASSES.length] ?? SPIT_CLASSES[0]);
  };

  const rest = (face: number): void => {
    stop();
    const arena = measure();
    root.classList.toggle(AWAY_CLASS, !arena?.hasSlot);
    if (!arena) return;
    solid.draw({ ...arena.landing, scale: 1, orientation: landingOrientation(face) }, arena.radius);
  };

  const tumble = (face: number): void => {
    if (reducedMotion.matches) {
      rest(face);
      return;
    }
    stop();
    root.classList.remove(AWAY_CLASS, ...SPIT_CLASSES);
    const arena = measure();
    if (!arena) return;

    const inset = arena.radius * TUMBLE_WALL_INSET;
    const area = {
      left: inset,
      top: inset,
      right: arena.width - inset,
      bottom: arena.height - inset,
    };
    // The plan moves the start to the nearest point of the area.
    const plan = planTumble({
      area,
      start: arena.launch,
      landing: arena.landing,
      radius: arena.radius,
      face,
      rng: createRng(randomSeed()),
    });
    const startedAt = performance.now();
    /** The bounces that sounded. */
    let knocked = 0;
    const step = (): void => {
      const progress = (performance.now() - startedAt) / DIE_SPIN_MS;
      while (knocked < plan.bounces.length && (plan.bounces[knocked] ?? 1) <= progress) {
        playSound('dieBounce', 0, knocked);
        knocked += 1;
      }
      solid.draw(poseAt(plan, progress), arena.radius);
      frame = progress < 1 ? requestAnimationFrame(step) : 0;
    };
    step();
  };

  return { svg: root, tumble, rest, stop, label: solid.label, spit };
};
